import React, { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logError } from '../../utils/errorLog';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  /** Componente personalizado de fallback. Si no se pasa, usa el UI por defecto. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Nivel del boundary: 'page' muestra UI completa, 'section' muestra UI compacta */
  level?: 'app' | 'page' | 'section';
  /** Callback opcional cuando ocurre un error (para logging) */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// ── Fallback UIs ───────────────────────────────────────────────────────────────

function AppFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 p-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
        <AlertTriangle size={40} className="text-red-500" />
      </div>
      <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
        Algo salió mal
      </h1>
      <p className="mb-1 text-gray-500 dark:text-gray-400 max-w-md">
        La aplicación encontró un error inesperado. Puedes intentar recargar o volver al inicio.
      </p>
      {import.meta.env.DEV && (
        <pre className="mt-4 mb-6 max-w-xl overflow-auto rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-left text-xs text-red-700 dark:text-red-300">
          {error.message}
        </pre>
      )}
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => { window.location.href = '/'; }}
          className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <Home size={16} /> Inicio
        </button>
        <button
          onClick={reset}
          className="flex items-center gap-2 rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
        >
          <RefreshCw size={16} /> Reintentar
        </button>
      </div>
    </div>
  );
}

function PageFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
        <AlertTriangle size={32} className="text-red-500" />
      </div>
      <h2 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
        Error al cargar la página
      </h2>
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
        Esta sección no pudo cargarse correctamente.
      </p>
      {import.meta.env.DEV && (
        <pre className="mb-4 max-w-md overflow-auto rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-left text-xs text-red-700 dark:text-red-300">
          {error.message}
        </pre>
      )}
      <button
        onClick={reset}
        className="flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
      >
        <RefreshCw size={14} /> Reintentar
      </button>
    </div>
  );
}

function SectionFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
      <AlertTriangle size={20} className="shrink-0 text-red-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-red-700 dark:text-red-300">
          Este componente no pudo cargarse
        </p>
        {import.meta.env.DEV && (
          <p className="mt-0.5 text-xs text-red-500 truncate">{error.message}</p>
        )}
      </div>
      <button
        onClick={reset}
        className="shrink-0 flex items-center gap-1 rounded-lg bg-red-100 dark:bg-red-800 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
      >
        <RefreshCw size={12} /> Reintentar
      </button>
    </div>
  );
}

// ── ErrorBoundary Class ────────────────────────────────────────────────────────

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorInfo: info });
    this.props.onError?.(error, info);

    // Persistir para observabilidad (recuperable desde localStorage en prod)
    logError(error.message, 'react', error.stack ?? info.componentStack ?? undefined);

    console.error('[ErrorBoundary] Error capturado:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    const { error } = this.state;
    const { children, fallback, level = 'page' } = this.props;

    if (error) {
      if (fallback) return fallback(error, this.reset);
      if (level === 'app') return <AppFallback error={error} reset={this.reset} />;
      if (level === 'section') return <SectionFallback error={error} reset={this.reset} />;
      return <PageFallback error={error} reset={this.reset} />;
    }

    return children;
  }
}

export default ErrorBoundary;
